import React, { useEffect, useRef, useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentReplyItem from './CommentReplyItem';
import EditDeleteMenuButton from '../button/EditDeleteMenuButton';
import AdminDeleteButton from '../button/AdminDeleteButton';
import CancelConfirmDialog from '../dialog/CancelConfirmDialog';
import theme from '../../theme';
import UserWithProfile from '../common/UserWithProfile';
import FavoriteButton from '../button/FavoriteButton';
import CommentReplyButton from '../button/CommentReplyButton';
import CommentEditForm from '../common/CommentEditForm';
import CommentReplyForm from '../common/CommentReplyForm';
import CommentContent from '../common/CommentContent';
import useCommentActions from '../../hooks/useCommentActions';
import { useState } from 'react';

const COMMENT_MAX_LENGTH = 5000;

const CommentItem = ({ comment, onReply, onEdit, onDelete, commentScrollId = null }) => {
  const commentRef = useRef(null);
  const [showAllReplies, setShowAllReplies] = useState(false);

  const {
    isEditing,
    editContent,
    setEditContent,
    isReplying,
    replyContent,
    setReplyContent,
    isLiked,
    likeCount,
    isCancelEditDialogOpen,
    isCancelReplyDialogOpen,
    isCommentAuthor,
    isAdmin,
    navigateWithScrollMemory,
    handleEditClick,
    handleEditSubmit,
    handleEditCancel,
    handleEditCancelConfirm,
    handleEditCancelCancel,
    handleReplyIconClick,
    handleReplySubmit,
    handleCancelReplyClick,
    handleCancelReplyCancel,
    handleCancelReplyConfirm,
    handleLikeToggle,
  } = useCommentActions({ comment, onEdit, onReply, replyTargetId: comment.id });

  // 대댓글 표시 로직
  const replies = useMemo(() => comment.replies || [], [comment.replies]);
  const visibleReplies = showAllReplies ? replies : replies.slice(0, 3);
  const hasMoreReplies = replies.length > 3;

  // commentScrollId가 현재 댓글인지 확인하고 스크롤
  useEffect(() => {
    if (commentScrollId === comment.id && commentRef.current) {
      setTimeout(() => {
        commentRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }, 200);
    }
  }, [commentScrollId, comment.id]);

  // commentScrollId가 대댓글의 ID인 경우 해당 대댓글을 자동으로 펼치기
  useEffect(() => {
    if (commentScrollId && replies.length > 0) {
      const targetReplyIndex = replies.findIndex((reply) => reply.id === commentScrollId);
      if (targetReplyIndex !== -1 && targetReplyIndex > 2) {
        setShowAllReplies(true);
      }
    }
  }, [commentScrollId, replies]);

  return (
    <Box ref={commentRef}>
      <Box py={{ xs: 1.5, md: 1.5, lg: 1.5 }}>
        {/* 1. 프로필 정보 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, md: 2.5 },
          }}
        >
          <UserWithProfile
            username={comment.user.nickname}
            profileImageUrl={comment.user.profileImageUrl}
            userNickname={comment.user.nickname}
            grade={comment.user.grade}
            onClick={() =>
              navigateWithScrollMemory(`/users/${encodeURIComponent(comment.user.nickname)}`)
            }
            variant="large"
            spacing={1}
          />
          {/* 어드민 삭제 버튼 */}
          {isAdmin && !isCommentAuthor && (
            <AdminDeleteButton variant="small" onDelete={() => onDelete(comment.id)} />
          )}
          {/* 작성자 메뉴 버튼 */}
          {isCommentAuthor && !comment.isDeleted && !isEditing && (
            <EditDeleteMenuButton
              variant="small"
              onEdit={handleEditClick}
              onDelete={() => onDelete(comment.id)}
            />
          )}
        </Box>

        {/* 2. 댓글 내용 */}
        {isEditing ? (
          <CommentEditForm
            editContent={editContent}
            setEditContent={setEditContent}
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
            maxLength={COMMENT_MAX_LENGTH}
          />
        ) : (
          <CommentContent
            content={comment.content}
            isDeleted={comment.isDeleted}
            mentionedUser={comment.mentionedUser}
          />
        )}

        {!isEditing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <Typography fontSize={14} color={theme.palette.black[1]} sx={{ whiteSpace: 'nowrap' }}>
              {comment.updatedAt}
            </Typography>
            {!comment.isDeleted && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: '8px',
                    alignSelf: 'center',
                    backgroundColor: theme.palette.grey.line,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <FavoriteButton
                    isLiked={isLiked}
                    onToggle={handleLikeToggle}
                    likeCount={likeCount}
                  />
                  <CommentReplyButton count={replies.length} onClick={handleReplyIconClick} />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
      {/* 대댓글 작성 폼 */}
      <CommentReplyForm
        isOpen={isReplying}
        replyContent={replyContent}
        setReplyContent={setReplyContent}
        onSubmit={handleReplySubmit}
        onCancel={handleCancelReplyClick}
        maxLength={COMMENT_MAX_LENGTH}
        mentionedNickname={comment.user.nickname}
      />

      {/* 대댓글 목록 */}
      {visibleReplies.length > 0 && (
        <Box bgcolor={theme.palette.grey.bg}>
          {visibleReplies.map((reply, index) => (
            <CommentReplyItem
              key={`${reply.id}-${index}`}
              comment={reply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              parentId={comment.id}
              commentScrollId={commentScrollId}
            />
          ))}

          {/* 더보기 버튼 */}
          {hasMoreReplies && (
            <Box
              display="flex"
              alignItems="center"
              onClick={() => setShowAllReplies(!showAllReplies)}
              sx={{ pl: { xs: 2, lg: 3 }, pb: { xs: 2, lg: 3 }, cursor: 'pointer' }}
              gap={1}
            >
              <Typography fontSize={14} color={theme.palette.black[4]}>
                {showAllReplies ? '접기' : `댓글 ${replies.length - 3}개 더보기`}
              </Typography>
              {showAllReplies ? (
                <ExpandLessIcon sx={{ fontSize: '14px' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: '14px' }} />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* 취소 확인 다이얼로그 */}
      <CancelConfirmDialog
        open={isCancelReplyDialogOpen}
        onClose={handleCancelReplyCancel}
        onConfirm={handleCancelReplyConfirm}
      />
      <CancelConfirmDialog
        open={isCancelEditDialogOpen}
        onClose={handleEditCancelCancel}
        onConfirm={handleEditCancelConfirm}
      />
    </Box>
  );
};

export default CommentItem;
